import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LineChart,
  Line,
  Legend,
} from "recharts";
import {
  RotateCcw,
  Download,
  Check,
  AlertCircle,
  Code,
  Activity,
} from "lucide-react";
import { toast } from "@shared/hooks/use-toast";
import { useGetMonitoring } from "@features/ix/erpIntegration/hooks";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { useGetChoices } from "@features/ix/hooks";

const formatTime = (dateStr?: string, timeStr?: string) => {
  if (!dateStr && !timeStr) return "";
  const dateString = timeStr
    ? `${dateStr || "2000/01/01"} ${timeStr}`
    : dateStr;
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return timeStr || dateStr || "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const MonitoringTab: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [period, setPeriod] = useState("24h");
  const { data: syncLogPeriodData } = useGetChoices("sync_log_period");

  const { data: monitoringData } = useGetMonitoring(id || "", period);

  const stats = useMemo(
    () => [
      {
        title: "Success rate",
        displayValue: `${monitoringData?.successRate ?? 0}%`,
        trend: 0,
        trendDirection: "up" as const,
        trendDurationDisplay: "vs prev period",
        icon: <Check className="h-6 w-6 text-emerald-500" />,
      },
      {
        title: "Failure rate",
        displayValue: `${monitoringData?.failureRate ?? 0}%`,
        trend: 0,
        trendDirection: "down" as const,
        trendDurationDisplay: "",
        icon: <AlertCircle className="h-6 w-6 text-red-500" />,
      },
      {
        title: "Avg API latency",
        displayValue: `${monitoringData?.avgLatencyMs ?? 0}ms`,
        trend: 0,
        trendDirection: "up" as const,
        trendDurationDisplay: "",
        icon: <Code className="h-6 w-6 text-[#0f766e]" />,
      },
      {
        title: "Throughput",
        displayValue: `${monitoringData?.throughputPerMin ?? 0}/min`,
        trend: 0,
        trendDirection: "up" as const,
        trendDurationDisplay: "",
        icon: <Activity className="h-6 w-6 text-blue-500" />,
      },
    ],
    [monitoringData],
  );

  const apiCallsData = useMemo(() => {
    const list = monitoringData?.apiCallsTimeseries;
    if (!list) return [];
    return list.map((d: any) => ({
      name: formatTime(d.ts || d.date, d.time),
      calls: d.count,
    }));
  }, [monitoringData]);

  const syncRatioData = useMemo(() => {
    const list = monitoringData?.failedVsSuccessTimeseries;
    if (!list) return [];
    return list.map((d: any) => ({
      name: formatTime(d.date || d.ts, d.time),
      success: d.succeeded,
      failed: d.failed,
    }));
  }, [monitoringData]);

  const latencyHistoryData = useMemo(() => {
    const list = monitoringData?.latencyPercentilesTimeseries;
    if (!list) return [];
    return list.map((d: any) => ({
      name: formatTime(d.date || d.ts, d.time),
      p50: Number(d.p50) || 0,
      p95: Number(d.p95) || 0,
      p99: Number(d.p99) || 0,
    }));
  }, [monitoringData]);

  const maxLatency = useMemo(() => {
    if (!latencyHistoryData.length) return 100;
    const max = Math.max(
      ...latencyHistoryData.map((d: any) => Math.max(d.p50, d.p95, d.p99)),
    );
    return Math.max(100, Math.ceil(max * 1.1));
  }, [latencyHistoryData]);

  const handleExport = () => {
    toast({
      title: "Monitoring Export",
      description: "Exporting monitoring performance metrics log...",
    });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Top Header Buttons Row */}
      <div className="flex items-center justify-end gap-3">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            {syncLogPeriodData?.map((d: any) => (
              <SelectItem key={d.key} value={d.key}>
                {d.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((card, idx) => (
          <QuickStatsCard
            key={idx}
            title={card.title}
            displayValue={card.displayValue}
            trend={card.trend}
            trendDirection={card.trendDirection}
            trendDurationDisplay={card.trendDurationDisplay}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Row of Two Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Calls over time */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">
              API calls over time
            </h3>
            <p className="text-xs text-gray-500">Selected period: {period}</p>
          </div>
          <div className="h-64 w-full">
            {apiCallsData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={apiCallsData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="callsGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#5850ec"
                        stopOpacity={0.85}
                      />
                      <stop
                        offset="100%"
                        stopColor="#5850ec"
                        stopOpacity={0.25}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="name"
                    stroke="#9ca3af"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip cursor={{ fill: "rgba(88, 80, 236, 0.05)" }} />
                  <Bar
                    dataKey="calls"
                    fill="url(#callsGradient)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-gray-500">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Failed vs successful sync */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">
              Failed vs successful sync
            </h3>
            <p className="text-xs text-gray-500">
              Stacked bar — selected period: {period}
            </p>
          </div>
          <div className="h-64 w-full">
            {syncRatioData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={syncRatioData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="successGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#5850ec"
                        stopOpacity={0.85}
                      />
                      <stop
                        offset="100%"
                        stopColor="#5850ec"
                        stopOpacity={0.4}
                      />
                    </linearGradient>
                    <linearGradient
                      id="failedGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#ef4444"
                        stopOpacity={0.85}
                      />
                      <stop
                        offset="100%"
                        stopColor="#ef4444"
                        stopOpacity={0.4}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="name"
                    stroke="#9ca3af"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="success"
                    stackId="a"
                    fill="url(#successGradient)"
                  />
                  <Bar
                    dataKey="failed"
                    stackId="a"
                    fill="url(#failedGradient)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-gray-500">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Latency History Line Chart */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
        <div>
          <h3 className="text-base font-bold text-gray-900">
            API latency (p50 / p95 / p99)
          </h3>
          <p className="text-xs text-gray-500">Selected period: {period}</p>
        </div>
        <div className="h-64 w-full">
          {latencyHistoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={latencyHistoryData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  stroke="#9ca3af"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#9ca3af"
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  unit="ms"
                  domain={[0, maxLatency]}
                />
                <Tooltip />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ fontSize: 10, fontWeight: 700 }}
                />
                <Line
                  type="monotone"
                  dataKey="p50"
                  stroke="#5850ec"
                  strokeWidth={2}
                  dot={false}
                  name="p50"
                />
                <Line
                  type="monotone"
                  dataKey="p95"
                  stroke="#10b981"
                  strokeWidth={3}
                  strokeDasharray="8 6"
                  dot={false}
                  name="p95"
                />
                <Line
                  type="monotone"
                  dataKey="p99"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="3 5"
                  strokeDashoffset={4}
                  dot={false}
                  name="p99"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-gray-500">
              No data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonitoringTab;
